import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Typography, Breadcrumb, Card, Row, Col, Statistic, Button, Modal, Empty } from 'antd';
import { FilePdfOutlined, BarChartOutlined } from '@ant-design/icons';
import { PieChart, Pie, Cell, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ExpensesTab from './ExpensesTab';
import SalesTab from '../Sales/SalesTab';
import { getExpenseSellingApi, getCropByIdApi } from '../../api/crops';
import { formatCurrency } from '../../utils/formatters';
import { generateCropPDF } from '../../utils/pdfExport';
import { AREA_UNITS, CROP_NAME_IMAGE } from '../../utils/constants';
import dayjs from 'dayjs';

const { Title } = Typography;


export default function CropDetail() {
  const { farmId, cropId } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const fromCrops = location.state?.from === 'crops';
  const { user } = useSelector((s) => s.auth);
  const [summary, setSummary] = useState({ totalExpense: 0, totalSelling: 0 });
  const [expenses, setExpenses] = useState([]);
  const [sellings, setSellings] = useState([]);
  const [crop, setCrop] = useState(location.state?.crop || null);
  const [reportOpen, setReportOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem(`cropTab_${cropId}`) || 'expenses');

  const handleTabChange = (key) => {
    setActiveTab(key);
    sessionStorage.setItem(`cropTab_${cropId}`, key);
  };

  const fetchSummary = useCallback(() => {
    getExpenseSellingApi({ crop_id: cropId }).then((res) => {
      const d = res.data.data || {};
      const expList = d.expenses || [];
      const totalExpense = expList.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const totalSelling = (d.sellings || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
      setSummary({ totalExpense, totalSelling });
      setExpenses(expList);
      setSellings(d.sellings || []);
    }).catch(() => { });
  }, [cropId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    if (crop) return;
    getCropByIdApi({ crop_id: cropId }).then((res) => {
      const d = res.data?.data || res.data || {};
      setCrop(Array.isArray(d) ? d[0] : d);
    }).catch(() => { });
  }, [cropId]); // eslint-disable-line react-hooks/exhaustive-deps

  const profit = (summary.totalSelling || 0) - (summary.totalExpense || 0);

  const items = [
    { key: 'expenses', label: `💰 ${t('expenses')} (${formatCurrency(summary.totalExpense)})`, children: <ExpensesTab cropId={cropId} externalTotal={summary.totalExpense} onRefresh={fetchSummary} /> },
    { key: 'sales', label: `📦 ${t('sales')} (${formatCurrency(summary.totalSelling)})`, children: <SalesTab cropId={cropId} externalTotal={summary.totalSelling} onRefresh={fetchSummary} /> },
  ];

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[
        ...(fromCrops
          ? [{ title: <Link to="/crops">Crops</Link> }]
          : [
            { title: <Link to="/farms">Farms</Link> },
            { title: <Link to={`/farms/${farmId}/farm`} state={{ years: location.state?.years }}>Farm Crops</Link> },
          ]
        ),
        { title: 'Detail' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {crop?.image && (
            <img src={CROP_NAME_IMAGE(crop.image)} alt={crop.crop_name} style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
          )}
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {crop?.crop_name || crop?.crop || 'Crop Detail'}
              {crop?.verity && <span style={{ fontSize: 15, fontWeight: 400, color: '#555', marginLeft: 6 }}>, {crop.verity}</span>}
            </Title>
            <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
              {[crop?.plot_name, crop?.area ? `${crop.area} ${AREA_UNITS[crop.area_unit] || crop.area_unit}` : null].filter(Boolean).join(' - ')}
              {crop?.farm_name && <span style={{ color: '#888' }}>{crop?.plot_name || crop?.area ? ', ' : ''}{crop.farm_name}</span>}
            </div>
            {crop?.crop_date && (() => {
              const today = dayjs();
              const hd = crop.harvest_date ? dayjs(crop.harvest_date) : null;
              const end = hd && !hd.isAfter(today, 'day') ? hd : today;
              const days = end.diff(dayjs(crop.crop_date), 'day');
              return <div style={{ fontSize: 13, color: '#1a6b2a', fontWeight: 500, marginTop: 2 }}>{dayjs(crop.crop_date).format('DD MMM YYYY')} · {days} days</div>;
            })()}
          </div>
        </div>
        <Button
          icon={<FilePdfOutlined />}
          loading={pdfLoading}
          onClick={async () => { setPdfLoading(true); await generateCropPDF({ crop, expenses, sellings, user }); setPdfLoading(false); }}
          style={{ borderColor: '#1a6b2a', color: '#1a6b2a' }}>
          {t('download_pdf')}
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: t('total_expense'), value: summary.totalExpense, color: '#fa8c16', bg: '#fff7e6' },
          { label: t('total_selling'), value: summary.totalSelling, color: '#52c41a', bg: '#f6ffed' },
          { label: profit >= 0 ? t('net_profit') : 'Net Loss', value: profit, color: profit >= 0 ? '#52c41a' : '#ff4d4f', bg: profit >= 0 ? '#f6ffed' : '#fff1f0' },
        ].map((item) => (
          <Col xs={24} sm={8} key={item.label}>
            <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }}>
              <Statistic title={item.label} value={formatCurrency(Math.abs(item.value))} valueStyle={{ color: item.color, fontSize: 18 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ borderRadius: 10 }}>
        <Tabs
          items={items}
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Button icon={<BarChartOutlined />} size="small" onClick={() => setReportOpen(true)}>
              Report
            </Button>
          }
        />
      </Card>

      <ExpenseReportModal open={reportOpen} onClose={() => setReportOpen(false)} expenses={expenses} />
    </div>
  );
}

const PIE_COLORS = ['#1a6b2a', '#52c41a', '#fa8c16', '#faad14', '#13c2c2', '#1677ff', '#722ed1', '#eb2f96', '#ff4d4f', '#a0d911'];

function ExpenseReportModal({ open, onClose, expenses }) {
  const data = Object.values(
    expenses.reduce((acc, e) => {
      const key = e.kharcho || 'Other';
      if (!acc[key]) acc[key] = { name: key, value: 0 };
      acc[key].value += Number(e.amount) || 0;
      return acc;
    }, {})
  ).sort((a, b) => b.value - a.value);

  return (
    <Modal title="Expense Breakdown" open={open} onCancel={onClose} footer={null} width={500}>
      {data.length === 0 ? (
        <Empty description="No expense data" />
      ) : (
        <ResponsiveContainer width="100%" height={340}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={120} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <ChartTooltip formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Amount']} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Modal>
  );
}
